import { ConfigProvider } from 'antd'
import zhCN from 'antd/lib/locale/zh_CN'
import CryptoJS from 'crypto-js'
import { ReactElement, ReactNode, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { cmsApi } from '@/api'
import PlanModal from '@/components/PlanModal'
import { UserStore } from '@/global-states'

const env = import.meta.env
const videSuffix = '?x-oss-process=video/snapshot,t_0,m_fast,ar_auto'

export const defaultPreviewText = '现在的一切都是为将来的梦想编织翅膀，让梦想在现实中展翅高飞。'

export const urlSource = (url: string, type?: string) => {
  let suffix = ''
  if (type === 'video' && isVideo(url)) {
    suffix = videSuffix
  }

  if (url?.indexOf('http') > -1 || url?.indexOf('//') > -1) {
    return `${url}${suffix}`
  }

  return `${env.VITE_API_BASE_URL_CDN}${url}${suffix}`
}

export const isVideo = (url: string) => {
  return /\.(mp4|mov|avi|wmv|flv|webm|mkv|3gp|mpeg|mpg|m4v)$/i.test(url)
}

export const urlOsskey = (osskey: string) => {
  return `${env.VITE_API_BASE_URL_CDN}${osskey.replace('public/', '')}`
}

export const tuple = <T extends any[]>(...elements: T) => {
  return elements
}

export const Render: (_: { children: () => ReactNode }) => ReactElement = (props: any) => <>{props.children()}</>

export const downlad = (urls: string, fileName: string) => {
  const x = new window.XMLHttpRequest()
  x.open('GET', urls, true)
  x.responseType = 'blob'

  x.onload = () => {
    const url = window.URL.createObjectURL(x.response)
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.download = fileName
    a.style.display = 'none'
    document.body.append(a)
    a.click()
    a.remove()
  }
  x.send()
  return x
}

export const findClosest = (arr: number[], target: number): number => {
  let closestIndex = 0
  let minDifference = Math.abs(arr[0] - target)

  for (let i = 1; i < arr.length; i++) {
    const difference = Math.abs(arr[i] - target)
    if (difference < minDifference) {
      minDifference = difference
      closestIndex = i
    }
  }

  return closestIndex
}

export const isMobile = () => {
  const userAgent: string = navigator.userAgent
  const mobileKeywords: string[] = ['Android', 'iPhone', 'iPad', 'Windows Phone', 'Mobile']
  const isMobile = mobileKeywords.some(function (keyword) {
    return userAgent.indexOf(keyword) !== -1
  })
  return isMobile
}

export const eventTracking = (event_name: string, properties = {}) => {
  const { uid, device_id } = UserStore
  const tracked = localStorage.getItem('event_track')

  const track = {
    event_name,
    di: device_id,
    client_time: parseInt((Date.now() / 1000).toFixed()),
    properties: {
      page_route: location.href,
      uid: uid,
      platform: 'web',
      ...properties
    }
  } as any

  if (!tracked) {
    firstEventTracking(JSON.parse(JSON.stringify(track)))
  }
  track.properties = JSON.stringify(track.properties)
  localStorage.setItem('event_track', '1')
  return cmsApi.post('event_logs', track)
}

export const firstEventTracking = (track: any) => {
  const { userPackage } = UserStore
  track.properties.system = navigator.userAgent
  track.properties.invite_code = sessionStorage.getItem('code') || userPackage?.invitor_aff_code
  track.properties.refer = document?.referrer

  cmsApi.post('event_logs', {
    ...track,
    event_name: 'first_event_track',
    properties: JSON.stringify(track.properties)
  })
}

export const Encrypt = (word: string) => {
  const key = CryptoJS.enc.Utf8.parse('GHJjPa9YBDaDZtzx')
  const iv = CryptoJS.enc.Utf8.parse('0')
  const encrypted = CryptoJS.AES.encrypt(word, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  })
  return encrypted.ciphertext.toString(CryptoJS.enc.Base64)
}

export const showPlanModal = () => {
  if (UserStore.planModalInstance) {
    UserStore.planModalInstance.setOpen(true)
    return
  }

  const div = document.createElement('div')
  document.body.appendChild(div)

  const root = ReactDOM.createRoot(div)

  root.render(
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#15B8F7' // 设置主题色
        }
      }}
    >
      <Render>
        {function showPlanModal() {
          const [open, setOpen] = useState(true)

          UserStore.planModalInstance = { setOpen }

          return (
            <PlanModal open={open} onCancel={() => setOpen(false)} onSuccess={() => UserStore.updateUserPackage()} />
          )
        }}
      </Render>
    </ConfigProvider>
  )
}

export const demoVideos = [
  {
    url: 'hf/local/demo-videos/1.mp4',
    ossKey: 'public/hf/local/demo-videos/1.mp4'
  },
  {
    url: 'hf/local/demo-videos/2.mp4',
    ossKey: 'public/hf/local/demo-videos/2.mp4'
  },
  {
    url: 'hf/local/demo-videos/3.mp4',
    ossKey: 'public/hf/local/demo-videos/3.mp4'
  }
]

export const demoAudios = [
  {
    id: 1,
    name: '坚强隐忍',
    url: 'hf/local/demo-audios/1.wav',
    ossKey: 'public/hf/local/demo-audios/1.wav'
  },
  {
    id: 2,
    name: '安心做自己',
    url: 'hf/local/demo-audios/2.wav',
    ossKey: 'public/hf/local/demo-audios/2.wav'
  },
  {
    id: 3,
    name: '给自己一点时间',
    url: 'hf/local/demo-audios/3.wav',
    ossKey: 'public/hf/local/demo-audios/3.wav'
  }
]

export const translateHtml = (text: string) => {
  if (text) {
    return text
      .replace(/<\/div>/g, '')
      .replace(/<div>/g, '\n')
      .replace(/<br>/g, '\n')
      .trim()
      .replace(/<(?!\/?(break|phoneme|category)\b)[^>]+>/g, '')
      .replace(/<break\s+class="[^"]*"\s+time="([^"]*)"\s+time-text="[^"]*"\s*>/g, '<break time="$1">')
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&nbsp;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/<(?!break\b[^>]*>)(?!\/break>)/g, '')
      .replace(/(<\/?break\b[^>]*>)|>/g, (match: any, p: any) => {
        // 如果匹配的是 <break> 或 </break>，则保留它
        if (p) return p
        // 否则，移除 '>'
        return ''
      })
  }
  return text
}
